import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Home,
  Edit,
  Save,
  Trash2,
  Check,
  X,
  ImageIcon,
  Video,
  Loader2,
  FileText,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import roomService, { Room, CreateRoomData } from '../services/roomService'

type RoomFormState = Omit<CreateRoomData, 'capacity'> & { capacity: number | '' }
import mediaService from '../services/mediaService'
import toast from 'react-hot-toast'
import ConfirmationDialog from '../components/ConfirmationDialog'
import { MEDIA_SECTIONS } from '../config/mediaValidation'
import { validateRoom } from '../validation'

const roomTypes = [
  { value: 'PRIVATE', label: 'Private', capacity: 1 },
  { value: 'DOUBLE', label: 'Shared - Double', capacity: 2 },
  { value: 'TRIPLE', label: 'Shared - Triple', capacity: 3 },
  { value: 'QUADRUPLE', label: 'Shared - Quadruple', capacity: 4 },
  { value: 'CUSTOM', label: 'Custom', capacity: null },
]

const RoomDetail: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const { selectedSchool } = useAuth()
  const [room, setRoom] = useState<Room | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<RoomFormState>({
    type: 'PRIVATE',
    name: '',
    capacity: 1,
    notes: '',
    images: [],
    video: '',
    isActive: true,
  })
  const [roomMediaUrlMap, setRoomMediaUrlMap] = useState<Record<string, string>>({})
  const [uploadingImage, setUploadingImage] = useState(false)
  const [uploadingVideo, setUploadingVideo] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)

  const getDisplayUrl = useCallback(
    (value: string): string => {
      if (!value) return value
      const fileKey =
        value.startsWith('http://') || value.startsWith('https://')
          ? mediaService.extractFileKeyFromUrl(value)
          : value
      if (!fileKey) return value
      return roomMediaUrlMap[fileKey] || value
    },
    [roomMediaUrlMap]
  )

  const fetchRoom = useCallback(async () => {
    if (!roomId) return
    setLoading(true)
    try {
      const data = await roomService.getRoomById(roomId)
      if (!data || typeof data.type === 'undefined') {
        toast.error('Invalid room data')
        navigate('/rooms')
        return
      }
      setRoom(data)
      setFormData({
        type: data.type,
        name: data.name ?? '',
        capacity: data.capacity ?? 1,
        notes: data.notes || '',
        images: data.images || [],
        video: data.video || '',
        isActive: data.isActive ?? true,
      })
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load room')
      navigate('/rooms')
    } finally {
      setLoading(false)
    }
  }, [roomId, navigate])

  useEffect(() => {
    fetchRoom()
  }, [fetchRoom])

  useEffect(() => {
    const collect = (value: string): string | null => {
      if (!value) return null
      if (value.startsWith('http://') || value.startsWith('https://')) {
        return mediaService.extractFileKeyFromUrl(value)
      }
      return value
    }
    const fileKeys = new Set<string>()
    room?.images?.forEach((url) => {
      const k = collect(url)
      if (k) fileKeys.add(k)
    })
    if (room?.video) {
      const k = collect(room.video)
      if (k) fileKeys.add(k)
    }
    formData.images?.forEach((url) => {
      const k = collect(url)
      if (k) fileKeys.add(k)
    })
    if (formData.video) {
      const k = collect(formData.video)
      if (k) fileKeys.add(k)
    }
    const map: Record<string, string> = {}
    fileKeys.forEach((key) => {
      map[key] = mediaService.getMediaUrlFromKey(key)
    })
    setRoomMediaUrlMap(map)
  }, [room, formData.images, formData.video])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!roomId || !room) return

    const capacityNum = formData.capacity === '' ? NaN : formData.capacity
    const roomErrors = validateRoom({ ...formData, capacity: capacityNum })
    if (Object.keys(roomErrors).length > 0) {
      toast.error(Object.values(roomErrors)[0])
      return
    }

    setSaving(true)
    try {
      await roomService.updateRoom(roomId, { ...formData, capacity: capacityNum })
      toast.success('Room updated successfully')
      setRoom({ ...room, ...formData, capacity: capacityNum })
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update room')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleStatus = async () => {
    if (!roomId || !room) return
    try {
      const updated = await roomService.toggleRoomStatus(roomId)
      setRoom(updated)
      setFormData((prev) => ({ ...prev, isActive: updated.isActive }))
      toast.success('Room status updated')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update status')
    }
  }

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files?.length || !selectedSchool) return
    setUploadingImage(true)
    try {
      for (let i = 0; i < files.length; i++) {
        const result = await mediaService.uploadFile(
          files[i],
          'generalImage',
          selectedSchool._id
        )
        if (result?.data?.fileKey) {
          setFormData((prev) => ({
            ...prev,
            images: [...(prev.images || []), result.data.fileKey],
          }))
        }
      }
      toast.success('Image(s) uploaded')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload image')
    } finally {
      setUploadingImage(false)
      e.target.value = ''
    }
  }

  const handleVideoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !selectedSchool) return
    setUploadingVideo(true)
    try {
      const result = await mediaService.uploadFile(
        file,
        'generalVideo',
        selectedSchool._id
      )
      if (result?.data?.fileKey) {
        setFormData((prev) => ({ ...prev, video: result.data.fileKey }))
        toast.success('Video uploaded')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload video')
    } finally {
      setUploadingVideo(false)
      e.target.value = ''
    }
  }

  const removeImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index),
    }))
  }

  const removeVideo = () => {
    setFormData((prev) => ({ ...prev, video: '' }))
  }

  const handleDeleteConfirm = async () => {
    if (!roomId) return
    setDeleting(true)
    try {
      await roomService.deleteRoom(roomId)
      toast.success('Room deleted')
      navigate('/rooms')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete room')
    } finally {
      setDeleting(false)
      setShowDeleteDialog(false)
    }
  }

  if (loading || !room) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  const displayImages = formData.images || []
  const displayVideo = formData.video

  return (
    <div className="space-y-6">
      {/* Back + header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/rooms')}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
              <Home className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{room.name}</h1>
              <p className="text-sm text-gray-500 capitalize">
                {room.type.toLowerCase().replace('-', ' ')} · {room.capacity} people
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleStatus}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              room.isActive
                ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {room.isActive ? <Check size={18} /> : <X size={18} />}
            {room.isActive ? 'Active' : 'Inactive'}
          </button>
          <button
            onClick={() => setShowDeleteDialog(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
          >
            <Trash2 size={18} />
            Delete
          </button>
        </div>
      </div>

      {/* Media section */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-primary-600" />
            Photos &amp; Video
          </h2>
        </div>
        <div className="p-6">
          {displayImages.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Images</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {displayImages.map((url, index) => (
                  <div
                    key={index}
                    className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50"
                  >
                    <img
                      src={getDisplayUrl(url)}
                      alt={`Room ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          {displayVideo && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Video</h3>
              <div className="rounded-lg overflow-hidden border border-gray-200 bg-black max-w-2xl">
                <video
                  src={getDisplayUrl(displayVideo)}
                  controls
                  className="w-full aspect-video"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          )}
          {displayImages.length === 0 && !displayVideo && (
            <p className="text-sm text-gray-500">No photos or video added yet. Add them in the form below.</p>
          )}
        </div>
      </motion.section>

      {/* Edit form */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Edit className="h-5 w-5 text-primary-600" />
            Edit room details
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Room type *</label>
              <select
                value={formData.type}
                onChange={(e) => {
                  const selectedType = e.target.value as Room['type']
                  const selectedRoomType = roomTypes.find((rt) => rt.value === selectedType)
                  setFormData({
                    ...formData,
                    type: selectedType,
                    capacity:
                      selectedRoomType?.capacity !== null
                        ? selectedRoomType?.capacity ?? 1
                        : formData.capacity === '' || typeof formData.capacity === 'number'
                          ? formData.capacity
                          : 1,
                  })
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              >
                {roomTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Room name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., Deluxe Private Room"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Capacity (people) *</label>
            <input
              type="number"
              value={
                formData.type === 'CUSTOM' && formData.capacity === '' ? '' : formData.capacity
              }
              onChange={(e) => {
                if (formData.type !== 'CUSTOM') return
                const v = e.target.value
                if (v === '') {
                  setFormData({ ...formData, capacity: '' })
                  return
                }
                const n = parseInt(v, 10)
                if (!Number.isNaN(n)) setFormData({ ...formData, capacity: n })
              }}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              readOnly={formData.type !== 'CUSTOM'}
              className={`w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg ${
                formData.type === 'CUSTOM'
                  ? 'focus:ring-2 focus:ring-primary-500'
                  : 'bg-gray-50 text-gray-600 cursor-not-allowed'
              }`}
              min={1}
              max={20}
              required
            />
            {formData.type === 'CUSTOM' && (
              <p className="text-xs text-gray-500 mt-1">Enter the number of people this room can accommodate.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <FileText size={14} />
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              placeholder="Additional information about this room type..."
            />
          </div>

          {/* Room images upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Room images</label>
            <input
              ref={imageInputRef}
              type="file"
              accept={MEDIA_SECTIONS.roomImages.acceptImages}
              multiple
              className="hidden"
              onChange={handleImageSelect}
            />
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              disabled={uploadingImage}
              className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-400 hover:bg-primary-50/50 transition-colors disabled:opacity-50"
            >
              {uploadingImage ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ImageIcon className="h-4 w-4" />
              )}
              {uploadingImage ? 'Uploading...' : 'Add images'}
            </button>
            {displayImages.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {displayImages.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={getDisplayUrl(url)}
                      alt={`Room ${index + 1}`}
                      className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Room video upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Room video</label>
            <input
              ref={videoInputRef}
              type="file"
              accept={MEDIA_SECTIONS.roomVideo.acceptVideos}
              className="hidden"
              onChange={handleVideoSelect}
            />
            <button
              type="button"
              onClick={() => videoInputRef.current?.click()}
              disabled={uploadingVideo}
              className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-400 hover:bg-primary-50/50 transition-colors disabled:opacity-50"
            >
              {uploadingVideo ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Video className="h-4 w-4" />
              )}
              {uploadingVideo ? 'Uploading...' : 'Add video'}
            </button>
            {displayVideo && (
              <div className="mt-2 flex items-center gap-2">
                <Video className="h-5 w-5 text-primary-600" />
                <span className="text-sm text-gray-600">Video added</span>
                <button
                  type="button"
                  onClick={removeVideo}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700">
              Active (available for selection)
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate('/rooms')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
      </motion.section>

      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete room"
        message="Are you sure you want to delete this room? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="red"
        isLoading={deleting}
      />
    </div>
  )
}

export default RoomDetail
